import { Component, OnInit, inject } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { ToastService } from 'src/app/services/toast.service';
import { ClientService } from 'src/app/services/client.services';
import { Guid } from 'guid-typescript';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { GeolocationService } from 'src/app/services/geolocation.service';
import { WorkloadsService } from 'src/app/services/workloads.service';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';

@Component({
    selector: 'app-client-info-modal',
    templateUrl: './client-info-modal.component.html',
    styleUrls: ['./client-info-modal.component.scss'],
    imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatSelectModule
]
})
export class ClientInfoModalComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(MatDialogRef<ClientInfoModalComponent>);
  private readonly data = inject(MAT_DIALOG_DATA);
  private readonly toastr = inject(ToastService);
  private readonly clientSvc = inject(ClientService);
  private readonly geolocationSvc = inject(GeolocationService);
  private readonly workloadSvc = inject(WorkloadsService);

  form!: FormGroup;

  workloadId!: string;

  clients: any[] = [];
  displayClient = (client: any) => client?.name ?? '';
  filteredClients: any[] = [];

  addresses: any[] = [];

  private cityCache = new Map<Guid, any>();

  ngOnInit(): void {
    this.workloadId = this.data?.workloadId;
    this.initForm();

    this.form.get('clientId')?.valueChanges.subscribe((client: any) => {
      if (client && client.id) {
        this.loadAddresses(client.id);
      } else {
        this.addresses = [];
      }
    });
  }

  private initForm(): void {
    this.form = this.fb.group({
      clientId: [null, Validators.required],
      addressId: [null, Validators.required]
    });
  }

  onClientSearch(text: string): void {
    text = text || '';
    if (text.length < 3) {
      this.filteredClients = [];
      return;
    }

    this.clientSvc.search(text).subscribe({
      next: (resp) => (this.filteredClients = resp),
      error: () => this.toastr.error('Error', 'No se pudieron cargar los clientes.')
    });
  }

  onClientSelected(client: any): void {
    this.form.patchValue({ clientId: client });
    if (client && client.id) {
      this.loadAddresses(client.id);
    }
  }

  loadAddresses(clientId: Guid): void {
    this.clientSvc.getAddresses(clientId).pipe(
      switchMap((addrs) => {
        if (!addrs?.length) return of([]);

        const requests = addrs.map(a => this.cityCache.has(a.cityId)
          ? of({ ...a, ...this.cityCache.get(a.cityId) })
          : this.geolocationSvc.getCity(a.cityId, true).pipe(
            map(city => {
              const geo = {
                cityName: city.description,
                stateName: city.state?.description,
                countryName: city.country?.description
              };

              this.cityCache.set(a.cityId, geo);

              return { ...a, ...geo };
            }),
            catchError(() => of(a))
          )
        );

        return forkJoin(requests);
      })
    )
      .subscribe((result) => this.addresses = result);
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  save(): void {
    if (this.form.invalid) {
      this.toastr.warning('Atención', 'Completá los campos requeridos.');
      return;
    }

    const client = this.form.value.clientId;
    const addressId = this.form.value.addressId;

    const payload = {
      clientId: client.id,
      addressId: addressId
    };

    this.workloadSvc.updateClientInfo(this.workloadId, payload).subscribe({
      next: () => {
        this.toastr.success('Cliente', 'Cliente actualizado correctamente.');

        this.dialogRef.close({
          client: client,
          addressId: addressId
        });
      },
      error: () => {
        this.toastr.error('Error', 'No se pudo guardar el cliente.');
      }
    });
  }

  formatAddress(a: any): string {
    if (!a) return '';

    const parts: string[] = [];

    // Calle + número
    if (a.street) {
      parts.push(`${a.street}${a.number ? ' ' + a.number : ''}`);
    }

    // Barrio
    if (a.neighborhood) parts.push(a.neighborhood);

    // Manzana
    if (a.block) parts.push(`Mz ${a.block}`);

    // Parcela
    if (a.parcel) parts.push(`Pc ${a.parcel}`);

    // Ciudad, provincia, país (si ya los completaste con la API)
    const geoParts: string[] = [];

    if (a.cityName) geoParts.push(a.cityName);
    if (a.stateName) geoParts.push(a.stateName);
    if (a.countryName) geoParts.push(a.countryName);

    if (geoParts.length) parts.push(geoParts.join(', '));

    return parts.join(' - ');
  }
}